import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { CheckoutTier } from "./checkoutFlowSlice";

export interface DowngradeModalState {
  show: boolean;
  targetTier: CheckoutTier | null;
}

export interface DowngradeSuccessModalState {
  show: boolean;
  effectiveDate: string | null;
}

export interface CancelConfirmModalState {
  show: boolean;
}

export interface CancelSuccessModalState {
  show: boolean;
  effectiveDate: string | null;
}

export interface ModalSliceState {
  draftDocumentModalOpen: boolean;
  draftModalDraftType: string;
  showExploreMoreModal: boolean;
  showPhoneCollectionModal: boolean;
  docsInChatOpen: boolean;
  downgradeModal: DowngradeModalState;
  downgradeSuccessModal: DowngradeSuccessModalState;
  cancelConfirmModal: CancelConfirmModalState;
  cancelSuccessModal: CancelSuccessModalState;
  restartConfirmModal: { show: boolean };
  restartSuccessModal: { show: boolean };
  showAccountModal: boolean;
  accountModalTab: string | null;
  showContactHelpModal: boolean;
  showSecurityModal: boolean;
  showUseInhouseDirectlyModal: boolean;
}

const initialState: ModalSliceState = {
  draftDocumentModalOpen: false,
  draftModalDraftType: "",
  showExploreMoreModal: false,
  showPhoneCollectionModal: false,
  docsInChatOpen: false,
  downgradeModal: { show: false, targetTier: null },
  downgradeSuccessModal: { show: false, effectiveDate: null },
  cancelConfirmModal: { show: false },
  restartConfirmModal: { show: false },
  restartSuccessModal: { show: false },
  showAccountModal: false,
  accountModalTab: null,
  showContactHelpModal: false,
  showSecurityModal: false,
  cancelSuccessModal: { show: false, effectiveDate: null },
  showUseInhouseDirectlyModal: false,
};

const modalSlice = createSlice({
  name: "modalSlice",
  initialState,
  reducers: {
    setDraftDocumentModalOpen: (state, action: PayloadAction<boolean>) => {
      state.draftDocumentModalOpen = action.payload;
    },
    setonlyDraftType: (state, action: PayloadAction<string>) => {
      state.draftModalDraftType = action.payload;
    },
    setDraftModalDraftType: (state, action: PayloadAction<string>) => {
      state.draftModalDraftType = action.payload;
      state.draftDocumentModalOpen = true;
    },
    setShowExploreMoreModal: (state, action: PayloadAction<boolean>) => {
      state.showExploreMoreModal = action.payload;
    },
    setShowPhoneCollectionModal: (state, action: PayloadAction<boolean>) => {
      state.showPhoneCollectionModal = action.payload;
    },
    setDocsInChatOpen: (state, action: PayloadAction<boolean>) => {
      state.docsInChatOpen = action.payload;
    },
    showDowngradeModal: (state, action: PayloadAction<DowngradeModalState>) => {
      state.downgradeModal = action.payload;
    },
    setDowngradeSuccessModal: (state, action: PayloadAction<DowngradeSuccessModalState>) => {
      state.downgradeSuccessModal = action.payload;
    },
    setCancelConfirmModal: (state, action: PayloadAction<CancelConfirmModalState>) => {
      state.cancelConfirmModal = action.payload;
    },
    setCancelSuccessModal: (state, action: PayloadAction<CancelSuccessModalState>) => {
      state.cancelSuccessModal = action.payload;
    },
    setRestartConfirmModal: (state, action: PayloadAction<{ show: boolean }>) => {
      state.restartConfirmModal = action.payload;
    },
    setRestartSuccessModal: (state, action: PayloadAction<{ show: boolean }>) => {
      state.restartSuccessModal = action.payload;
    },
    setShowAccountModal: (state, action: PayloadAction<boolean>) => {
      state.showAccountModal = action.payload;
      if (!action.payload) state.accountModalTab = null;
    },
    setAccountModalTab: (state, action: PayloadAction<string | null>) => {
      state.accountModalTab = action.payload;
    },
    setShowContactHelpModal: (state, action: PayloadAction<boolean>) => {
      state.showContactHelpModal = action.payload;
    },
    setShowSecurityModal: (state, action: PayloadAction<boolean>) => {
      state.showSecurityModal = action.payload;
    },
    setShowUseInhouseDirectlyModal: (state, action: PayloadAction<boolean>) => {
      state.showUseInhouseDirectlyModal = action.payload;
    },
  },
});

export default modalSlice.reducer;
export const {
  setDraftDocumentModalOpen,
  setDraftModalDraftType,
  setShowExploreMoreModal,
  setonlyDraftType,
  setShowPhoneCollectionModal,
  setDocsInChatOpen,
  showDowngradeModal,
  setDowngradeSuccessModal,
  setCancelConfirmModal,
  setCancelSuccessModal,
  setRestartConfirmModal,
  setRestartSuccessModal,
  setShowAccountModal,
  setAccountModalTab,
  setShowContactHelpModal,
  setShowSecurityModal,
  setShowUseInhouseDirectlyModal,
} = modalSlice.actions;
